import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CheckCircle, XCircle, ArrowRight, FileText, Briefcase, RefreshCw, HeartHandshake, Calendar, Users, FileCheck, Send } from "lucide-react";
import Layout from "@/components/Layout";
import { HeroImage } from "@/components/HeroImage";

const Services = () => {
  const servicesOffered = [
    "Individual Federal & State Returns (Form 1040)",
    "Sole Proprietor / Schedule C Returns",
    "Amended Returns",
    "Year-Round Support",
  ];

  const notOffered = [
    "Partnerships",
    "Corporations",
    "Trusts",
  ];

  const processSteps = [
    { number: "1", title: "Book", description: "Schedule your convenient appointment online" },
    { number: "2", title: "Meet", description: "Share your documents and discuss your situation" },
    { number: "3", title: "Review", description: "Review your return together and ask questions" },
    { number: "4", title: "File", description: "Relax while we securely file your return" },
  ];

  return (
    <Layout>
      {/* Header Section */}
      <section className="relative bg-gradient-to-br from-primary via-secondary to-accent text-primary-foreground overflow-hidden">
        <div className="absolute inset-0">
          <HeroImage
            src="/images/hero-services.jpg"
            alt="The Tiny Tax Lady - fast digital tax filing"
            priority={true}
            width={1920}
            height={800}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-primary/40 via-secondary/40 to-accent/40" />
        </div>
        
        <div className="container mx-auto px-4 py-16 md:py-20 relative z-10">
          <div className="max-w-3xl mx-auto text-center space-y-4">
            <h1 className="text-4xl md:text-5xl font-bold drop-shadow-lg">
              Professional Tax Preparation Made Simple
            </h1>
            <p className="text-xl text-primary-foreground/90">
              The Tiny Tax Lady specializes in accurate, affordable tax filing for individuals and sole proprietors. Whether you're filing a simple W-2 return or reporting self-employment income, you'll get personalized service from start to finish.
            </p>
          </div>
        </div>
      </section>

      {/* Services Offered */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-background via-muted/20 to-background relative overflow-hidden">
        {/* Enhanced animated background */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-10 right-10 w-96 h-96 bg-secondary/30 rounded-full blur-3xl animate-blob"></div>
          <div className="absolute bottom-10 left-10 w-72 h-72 bg-accent/30 rounded-full blur-3xl animate-blob animation-delay-2000"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-primary/20 rounded-full blur-3xl animate-blob animation-delay-4000"></div>
        </div>
        <div className="absolute inset-0 opacity-10 dot-grid"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12 animate-fade-in">
              <div className="inline-block mb-4">
                <span className="px-4 py-2 bg-gradient-to-r from-secondary/20 to-accent/20 rounded-full text-sm font-semibold text-secondary border border-secondary/20 shadow-lg">
                  What We Offer
                </span>
              </div>
              <h2 className="text-4xl md:text-6xl font-black mb-6 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent drop-shadow-2xl animate-shimmer relative">
                Services Offered
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-transparent via-accent to-transparent"></div>
              </h2>
              <p className="text-muted-foreground text-lg">
                Comprehensive tax preparation for individuals and sole proprietors
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {servicesOffered.map((service, index) => {
                const icons = [
                  <FileText className="h-7 w-7 text-secondary drop-shadow-lg" />,
                  <Briefcase className="h-7 w-7 text-secondary drop-shadow-lg" />,
                  <RefreshCw className="h-7 w-7 text-secondary drop-shadow-lg" />,
                  <HeartHandshake className="h-7 w-7 text-secondary drop-shadow-lg" />
                ];
                
                return (
                  <Card 
                    key={index} 
                    className="group relative p-6 hover:shadow-2xl hover:shadow-secondary/20 transition-all duration-500 hover:-translate-y-2 hover:rotate-1 border-2 hover:border-secondary/50 bg-gradient-to-br from-card via-card to-card/80 backdrop-blur-sm animate-fade-in overflow-hidden"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    {/* Accent bar */}
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-secondary to-accent transform origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500"></div>
                    
                    {/* Shine effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                    
                    <div className="flex items-start gap-4 relative z-10">
                      <div className="flex-shrink-0 p-3 bg-gradient-to-br from-secondary/30 to-secondary/10 rounded-xl group-hover:scale-125 group-hover:rotate-12 transition-all duration-500 shadow-lg">
                        {icons[index]}
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-lg group-hover:text-secondary transition-colors">{service}</p>
                        <div className="h-0.5 w-0 bg-gradient-to-r from-secondary to-accent group-hover:w-full transition-all duration-500 mt-2"></div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            <div className="relative">
              {/* "Most Popular" Badge */}
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20">
                <div className="px-6 py-2 bg-gradient-to-r from-accent via-secondary to-accent rounded-full text-white font-bold text-sm shadow-xl animate-pulse-glow">
                  ⭐ Most Popular
                </div>
              </div>
              
              <Card className="p-10 bg-gradient-to-br from-accent/20 via-secondary/10 to-accent/20 border-4 border-accent/40 shadow-2xl hover:shadow-3xl transition-all duration-500 relative overflow-hidden group animate-pulse-glow">
                {/* Animated gradient border glow */}
                <div className="absolute inset-0 bg-gradient-to-r from-accent via-secondary to-accent opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500"></div>
                
                {/* Sparkle decorations */}
                <div className="absolute top-4 right-4 w-4 h-4 bg-accent rounded-full animate-twinkle"></div>
                <div className="absolute bottom-6 left-6 w-3 h-3 bg-secondary rounded-full animate-twinkle animation-delay-2000"></div>
                <div className="absolute top-1/2 right-10 w-2 h-2 bg-accent/60 rounded-full animate-twinkle animation-delay-4000"></div>
                
                {/* Shine sweep */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1500"></div>
                
                <div className="relative z-10">
                  <h3 className="font-bold text-3xl mb-6 text-primary bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent">
                    Starting Price
                  </h3>
                  <div className="flex items-baseline justify-center gap-2 mb-4">
                    <span className="text-muted-foreground text-xl">from</span>
                    <span className="font-black text-7xl bg-gradient-to-br from-accent via-secondary to-accent bg-clip-text text-transparent drop-shadow-2xl animate-shimmer">
                      $100
                    </span>
                  </div>
                  <div className="h-1 w-24 mx-auto bg-gradient-to-r from-transparent via-accent to-transparent mb-4"></div>
                  <p className="text-base text-muted-foreground text-center">
                    (Price varies by complexity - quotes provided upfront)
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-muted/50 via-background to-muted/50 relative overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 right-20 w-96 h-96 bg-primary/20 rounded-full blur-3xl animate-blob"></div>
          <div className="absolute bottom-20 left-20 w-80 h-80 bg-secondary/20 rounded-full blur-3xl animate-blob animation-delay-2000"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-accent/20 rounded-full blur-3xl animate-blob animation-delay-4000"></div>
        </div>
        <div className="absolute inset-0 opacity-5 dot-grid"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,hsl(var(--primary)/0.05),transparent_70%)]"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16 animate-fade-in relative">
              <div className="inline-block mb-4">
                <span className="px-4 py-2 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-full text-sm font-semibold text-primary border border-primary/20 shadow-lg">
                  How It Works
                </span>
              </div>
              <h2 className="text-4xl md:text-6xl font-black mb-4 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent drop-shadow-2xl animate-shimmer relative">
                The Process
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-transparent via-secondary to-transparent"></div>
              </h2>
              <p className="text-muted-foreground text-xl mt-6">Simple, straightforward, stress-free</p>
            </div>
            
            <div className="relative">
              {/* Enhanced connection line */}
              <div className="hidden md:block absolute top-24 left-[12.5%] right-[12.5%] h-1 bg-gradient-to-r from-secondary/30 via-accent to-secondary/30 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-shimmer"></div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                {processSteps.map((step, index) => {
                  const stepIcons = [Calendar, Users, FileCheck, Send];
                  const StepIcon = stepIcons[index];
                  
                  return (
                    <div 
                      key={index} 
                      className="animate-fade-in"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <Card className="group relative p-8 hover:shadow-2xl hover:shadow-primary/20 transition-all duration-500 hover:-translate-y-3 border-2 hover:border-primary/50 bg-gradient-to-br from-card via-card/95 to-card/90 backdrop-blur-sm overflow-hidden h-full">
                        {/* Shine effect */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                        
                        {/* Accent glow */}
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                        
                        <div className="relative z-10 text-center">
                          <div className="relative inline-block mb-6">
                            {/* Outer glow */}
                            <div className="absolute inset-0 bg-gradient-to-br from-secondary to-accent rounded-full blur-xl opacity-40 group-hover:opacity-70 group-hover:scale-125 transition-all duration-500"></div>
                            
                            {/* Sparkles */}
                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-accent rounded-full animate-twinkle"></div>
                            <div className="absolute -bottom-1 -left-1 w-2 h-2 bg-secondary rounded-full animate-twinkle animation-delay-2000"></div>
                            
                            {/* Number circle */}
                            <div className="relative inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-secondary via-accent to-primary text-white shadow-2xl group-hover:scale-125 group-hover:rotate-12 transition-all duration-500">
                              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-secondary via-accent to-primary opacity-0 group-hover:opacity-100 animate-spin-slow blur-sm"></div>
                              <span className="text-3xl font-black relative z-10">{step.number}</span>
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-center gap-2 mb-3">
                            <StepIcon className="w-5 h-5 text-primary group-hover:text-secondary transition-colors duration-300" />
                            <h3 className="font-bold text-2xl group-hover:text-primary transition-colors duration-300">
                              {step.title}
                            </h3>
                          </div>
                          <div className="h-0.5 w-0 mx-auto bg-gradient-to-r from-secondary to-accent group-hover:w-20 transition-all duration-500 mb-3"></div>
                          <p className="text-muted-foreground text-base leading-relaxed">{step.description}</p>
                        </div>
                      </Card>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Not Offered */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h3 className="text-2xl font-bold text-center mb-8">Services Not Offered</h3>
            <div className="flex flex-wrap justify-center gap-4">
              {notOffered.map((item, index) => (
                <Card key={index} className="p-4 bg-background">
                  <div className="flex items-center gap-2">
                    <XCircle className="h-5 w-5 text-muted-foreground" />
                    <span className="text-muted-foreground">{item}</span>
                  </div>
                </Card>
              ))}
            </div>
            <p className="text-center text-muted-foreground mt-6">
              Focused exclusively on individuals and sole proprietors for the best service
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-secondary via-secondary to-primary relative overflow-hidden">
        <div className="absolute inset-0">
          <HeroImage
            src="/images/cta-ready-to-start.jpg"
            alt="Welcoming professional tax office entrance"
            width={1920}
            height={800}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-secondary/40 via-secondary/40 to-primary/40" />
        </div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.1),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(255,255,255,0.05),transparent_50%)]"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center text-white space-y-6">
            <h2 className="text-3xl md:text-5xl font-bold animate-fade-in">
              Ready to File Your Taxes?
            </h2>
            <p className="text-xl text-white/90 animate-fade-in" style={{ animationDelay: "100ms" }}>
              Let's make this tax season easy and stress-free
            </p>
            <div className="animate-fade-in" style={{ animationDelay: "200ms" }}>
              <Button asChild size="lg" variant="secondary" className="text-lg shadow-xl hover:shadow-2xl hover:scale-110 transition-all duration-300 animate-float">
                <Link to="/schedule-appointment">Book Your Appointment</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Services;
